/**
 * Column-width config: sanitizer + calls-table defaults.
 *
 * ROOT INCIDENT (2026-07-01, prod P0 on #/calls — docs/REGRESSIONS.md):
 * while dragging a column resize handle the app blue-screened with
 *   TypeError: Cannot read properties of null (reading 'key')
 * The document-level mousemove handler queued a FUNCTIONAL setState updater
 * that re-read the mutable drag ref at replay time:
 *   setCallColWidths(prev => ({ ...prev, [resizingCallCol.current!.key]: w }))
 * mousemove updates are continuous-priority and replay LATER, inside React's
 * useState hook machinery on the next render of App. If mouseup landed in
 * that window it nulled the ref (`resizingCallCol.current = null`), so the
 * deferred updater dereferenced null — the `!` assertion hid it from tsc and
 * the `if (!ref.current) return` guard only protects the EVENT frame, never
 * the replay. FIX: snapshot the ref into a local const inside the handler and
 * let the updater close over the snapshot. NEVER read a mutable ref inside a
 * state updater. colWidths.test.ts bans `resizing*.current!` at the source
 * level (deals/projects/contacts shared the identical race).
 *
 * SECOND LAYER — heal persisted configs on load: the per-user column-width
 * configs persisted to localStorage (deals `botpros-deals-col-widths`,
 * projects `botpros-projects-col-widths`, contacts
 * `botpros-crm-column-widths`, leads `leadsColWidths`) outlive column
 * redesigns: renamed/removed keys linger forever, newly added columns are
 * missing, and deals/projects parsed the raw JSON with NO shape check at all
 * (`raw ? JSON.parse(raw) : {}`) — a stored `null`/number/array/string became
 * the state itself and any `widths[key]` lookup crashed the view.
 * sanitizeColWidths() makes every load path total: never throws, drops
 * unknown keys, fills missing keys from defaults, rejects junk values.
 * Column-key renames/additions must never crash a view.
 *
 * The #/calls table itself deliberately does NOT persist widths today; if
 * persistence is ever added it MUST load through sanitizeColWidths().
 */

/**
 * Default widths for the #/calls table. Single source of truth shared by the
 * App.tsx `callColWidths` state and asserted against the CallsList column
 * contract in colWidths.test.ts. Column order: subject | contact (person) |
 * phone (external party) | direction | duration | outcome | sentiment |
 * dealproject (deal/lead/project) | rec | assigned | date.
 * (`phone` + `dealproject` were added in the 2026-06-30 calls-list redesign —
 * commits 6c340af / 82aad02 — which split the old combined "Contact / Deal"
 * column.)
 */
export const CALL_COL_DEFAULT_WIDTHS: Record<string, number> = {
  subject: 240, contact: 150, phone: 130, direction: 90, duration: 80,
  outcome: 110, sentiment: 100, dealproject: 170, rec: 50, assigned: 90, date: 100,
}

/** Sane bounds for a persisted column width, px. Anything outside is junk
 * (0, negatives, 1e9, NaN, Infinity, strings) and falls back to the default.
 * MIN sits below every legitimate default (calls `rec` is 50; resize handles
 * clamp live drags at 60) so no honestly-persisted value is ever rejected. */
export const COL_WIDTH_MIN = 24
export const COL_WIDTH_MAX = 4000

/**
 * Sanitize a persisted column-width config against the current defaults.
 *
 * Accepts anything (raw localStorage string, pre-parsed object, null,
 * garbage) and ALWAYS returns a fresh, complete `Record<K, number>`:
 *  - never throws (corrupt JSON / wrong shapes are healed to defaults)
 *  - unknown keys are dropped (removed/renamed columns don't linger)
 *  - missing keys are filled from defaults (newly added columns just work)
 *  - non-finite / out-of-range / non-number values fall back per key
 */
export function sanitizeColWidths<K extends string>(
  raw: unknown,
  defaults: Record<K, number>,
): Record<K, number> {
  let parsed: unknown = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      // Mirrored to /api/client-events by src/reliability.ts — a corrupt
      // persisted config is rare and worth tracing (also the minification-
      // proof deploy marker for this fix).
      console.warn('sanitizeColWidths: healed corrupt column-width config', raw.slice(0, 80))
      parsed = null
    }
  }
  const isPlainObject = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
  if (!isPlainObject && parsed !== null && parsed !== undefined) {
    console.warn('sanitizeColWidths: healed corrupt column-width config', typeof parsed)
  }
  const src: Record<string, unknown> = isPlainObject ? (parsed as Record<string, unknown>) : {}
  const out = {} as Record<K, number>
  for (const key of Object.keys(defaults) as K[]) {
    const v = src[key]
    out[key] =
      typeof v === 'number' && Number.isFinite(v) && v >= COL_WIDTH_MIN && v <= COL_WIDTH_MAX
        ? v
        : defaults[key]
  }
  return out
}
